/* eslint-disable max-lines */
import { Button, InputField, Textarea } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import { Mic, Square } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMicRecorder } from '../audio-recorder/useMicRecorder';
import { DYNAMIC_TEMPLATE } from '../audio-recorder/util';
import {
  DYNAMIC_MESSAGE_BOX,
  FIELD_CLASS,
  FIELD_LABEL,
} from './sop-card-utils';
import { AudioRow, PreviewPlayer } from './TalkdownAudio';

interface MessageFieldProps {
  value: string;
  onChange: (value: string) => void;
  isLoading: boolean;
  resolveAudio: () => Promise<{ url: string } | undefined>;
  text: string;
}

export function DynamicMessage(props: MessageFieldProps) {
  const { value, onChange, isLoading, resolveAudio, text } = props;

  const words = DYNAMIC_TEMPLATE.text.split(' ');
  const injectAt = DYNAMIC_TEMPLATE.inputAfterWordCount ?? 0;
  const rest = words.slice(injectAt).join(' ');

  return (
    <div className="flex flex-col gap-[15px]">
      <div className="space-y-2">
        <div className={FIELD_LABEL}>Select a message</div>
        <InputField
          type="text"
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          placeholder="eg: Red shirt, with a bag"
        />
        <div className={DYNAMIC_MESSAGE_BOX}>
          {words.slice(0, injectAt).join(' ')}{' '}
          <span className="text-primary-600 dark:text-primary-500 font-bold">
            {value.trim() ? `"${value.trim()}"` : '"Red shirt"'}
          </span>
          {/^[,.!?]/.test(rest) ? rest : ` ${rest}`}
        </div>
      </div>
      <PreviewPlayer
        key={text}
        isLoading={isLoading}
        resolveAudio={resolveAudio}
        disabled={!text}
      />
    </div>
  );
}

const TEXTAREA_CLASS = clsx(
  FIELD_CLASS,
  'block resize-none overflow-hidden shadow-sm'
);

export function CustomMessage(props: MessageFieldProps) {
  const { value, onChange, isLoading, resolveAudio, text } = props;

  return (
    <div className="flex flex-col gap-[15px]">
      <div className="space-y-2">
        <div className={FIELD_LABEL}>Type your message</div>
        <Textarea
          value={value}
          onChange={onChange}
          rows={3}
          autoGrow
          className={TEXTAREA_CLASS}
          placeholder="e.g. You are in a restricted area. Please leave immediately."
        />
      </div>
      <PreviewPlayer
        key={text}
        isLoading={isLoading}
        resolveAudio={resolveAudio}
        disabled={!text}
      />
    </div>
  );
}

interface MicCaptureProps {
  onStopped: (file: File) => void;
  onCancel: () => void;
}

function MicCapture({ onStopped, onCancel }: MicCaptureProps) {
  const { state, stop } = useMicRecorder(onCancel);
  const reported = useRef(false);

  useEffect(() => {
    if (state.kind === 'stopped' && !reported.current) {
      reported.current = true;
      onStopped(state.file);
    }
  }, [state, onStopped]);

  return (
    <div className="flex flex-col items-center gap-2 py-2">
      {state.kind === 'acquiring' ? (
        <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs">
          Starting microphone…
        </span>
      ) : (
        <>
          <Button
            variant="icon"
            onClick={stop}
            title="Stop recording"
            className={clsx(
              MIC_BUTTON_CLASS,
              'bg-primary-500 hover:bg-primary-600 !text-white'
            )}
          >
            <Mic className="size-5" />
          </Button>
          <div className="flex items-center gap-2">
            <span className="relative inline-flex size-4">
              <Square className="text-alert-400 absolute size-4 animate-ping fill-current opacity-75" />
              <Square className="text-alert-500 relative size-4 fill-current" />
            </span>
            <span className="text-onlight-text-2 dark:text-ondark-text-2 whitespace-pre text-xs">
              Recording...
            </span>
          </div>
        </>
      )}
    </div>
  );
}

const MIC_BUTTON_CLASS =
  'outline-primary-500 size-10 !rounded-full !p-0 outline outline-1';

interface RecorderProps {
  file?: File;
  onRecorded: (file?: File) => void;
}

export function TalkdownRecorder({ file, onRecorded }: RecorderProps) {
  const [isArmed, setIsArmed] = useState(false);
  const [url, setUrl] = useState('');

  const onStopped = useCallback(
    (recorded: File) => {
      onRecorded(recorded);
      setIsArmed(false);
    },
    [onRecorded]
  );
  const onCancel = useCallback(() => setIsArmed(false), []);

  useEffect(() => {
    if (!file) {
      setUrl('');
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <div className="space-y-2">
      <div className={FIELD_LABEL}>
        Describe the person, request him to leave
      </div>
      {isArmed ? (
        <MicCapture onStopped={onStopped} onCancel={onCancel} />
      ) : (
        <div className="flex flex-col items-center gap-2 py-2">
          <Button
            variant="icon"
            onClick={() => {
              onRecorded(undefined);
              setIsArmed(true);
            }}
            className={clsx(
              MIC_BUTTON_CLASS,
              'bg-primary-500/10 !text-primary-600 dark:!text-primary-400 hover:bg-primary-500/20'
            )}
            title="Start recording"
          >
            <Mic className="size-5" />
          </Button>
          <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs">
            {file ? 'Tap to record again' : 'Tap to start recording'}
          </span>
        </div>
      )}
      {url && <AudioRow src={url} />}
    </div>
  );
}

export default DynamicMessage;
